import { gql } from '@apollo/client';
import { client } from '../../../../../graphql/client';
import i18n from '../../../../../i18n';

/**
 * 导出仓库派送线路 Excel（后端 EasyExcel+POI 生成，含操作列/分配车队列下拉、多语表头、邮编列加宽）。
 * 返回 Base64 编码的 xlsx 字节。
 */
export const DOWNLOAD_AREA_ROUTES_EXCEL = gql`
    query DownloadAreaRoutesExcel($warehouseId: Long!, $language: String!) {
        downloadAreaRoutesExcel(warehouseId: $warehouseId, language: $language)
    }
`;

export interface DownloadAreaRoutesExcelResult {
    downloadAreaRoutesExcel: string; // Base64 编码的 Excel 文件内容
}

// 后端期望 'zh'/'en'/'es'，前端语言如 'zh-CN' 需归一化为前两位
const normalizeLanguage = (lang: string | undefined | null): string => {
    if (!lang) return 'en';
    return lang.toLowerCase().split('-')[0];
};

/**
 * 触发浏览器下载派送线路导出文件。
 * @param warehouseId 仓库ID
 * @param fileName 下载文件名（含 .xlsx 后缀）
 */
export const downloadAreaRoutesExcel = async (warehouseId: number, fileName: string): Promise<void> => {
    const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language || 'en');
    const { data } = await client.query<DownloadAreaRoutesExcelResult>({
        query: DOWNLOAD_AREA_ROUTES_EXCEL,
        variables: { warehouseId, language },
        fetchPolicy: 'no-cache',
    });

    const base64Data = data?.downloadAreaRoutesExcel;
    if (!base64Data) {
        throw new Error('Failed to get export data');
    }

    const binaryData = atob(base64Data);
    const bytes = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};
